import React, { useState } from 'react';

import { Menu, MenuItem, Typography } from '@mui/material';
import { UploadFilesPicker } from 'src/components/FilestackPicker';
import { NameDialog, NameDialogVariant } from 'src/components/generic';
import { AddIcon } from 'src/components/icons/AddIcon';
import { DocumentIcon } from 'src/components/icons/DocumentIcon';
import { TemplateIcon } from 'src/components/icons/TemplateIcon';
import { UploadIcon } from 'src/components/icons/UploadIcon';
import { AddWorkflowDocumentDialog } from 'src/components/workflow/AddWorkflowDocumentDialog';
import { Apollo } from 'src/gql/generated';
import { FileStackDomContainer } from 'src/helpers';
import { useCreateDocument } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  menuIcon: {
    marginRight: theme.spacing(1),
  },
}));

export interface AddWorkflowFilesMenuProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  open: boolean;
  workflow: Pick<Apollo.Workflow, 'variant'> | null | undefined;
  workflowId: UUID;
  dense?: boolean;
  documentRole?: Apollo.WorkflowFileDocumentRole;
  uploadItemDataCy?: string;
  uploadPickerDataCy?: string;
}

/**
 * Reusable menu + dialogs for adding documents to a workflow (create blank, upload,
 * pick from repository, use template). Compose with your own trigger (e.g. button).
 */
export const AddWorkflowFilesMenu = React.memo(function AddWorkflowFilesMenu({
  anchorEl,
  dense,
  documentRole,
  onClose,
  open,
  uploadItemDataCy,
  uploadPickerDataCy,
  workflow,
  workflowId,
}: AddWorkflowFilesMenuProps): JSX.Element {
  const { classes } = useStyles();
  const [isFilePickerOpen, setFilePickerOpen] = useState(false);
  const [repoPickerVariant, setRepoPickerVariant] = useState<Apollo.DocumentVariant | null>(null);
  const [createDocumentDialogOpen, setCreateDocumentDialogOpen] = useState(false);
  const [addDocumentsToWorkflow] = Apollo.useAddDocumentsToWorkflowMutation();
  const { createDocument } = useCreateDocument();
  const isTemplateWorkflow = workflow?.variant === Apollo.WorkflowVariant.Template;

  const closeMenu = () => {
    onClose();
  };

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        open={open}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        onClose={closeMenu}
      >
        <MenuItem
          data-cy={uploadItemDataCy}
          dense={dense}
          onClick={() => {
            closeMenu();
            setFilePickerOpen(true);
          }}
        >
          <UploadIcon className={classes.menuIcon} />
          <Typography>Upload files</Typography>
        </MenuItem>
        {!isTemplateWorkflow && (
          <MenuItem
            dense={dense}
            onClick={() => {
              closeMenu();
              setRepoPickerVariant(Apollo.DocumentVariant.Template);
            }}
          >
            <TemplateIcon className={classes.menuIcon} />
            <Typography>Use template</Typography>
          </MenuItem>
        )}
        <MenuItem
          dense={dense}
          onClick={() => {
            closeMenu();
            setRepoPickerVariant(
              isTemplateWorkflow ? Apollo.DocumentVariant.Template : Apollo.DocumentVariant.Document
            );
          }}
        >
          <DocumentIcon className={classes.menuIcon} />
          <Typography>Pick from repository</Typography>
        </MenuItem>
        <MenuItem
          dense={dense}
          onClick={() => {
            closeMenu();
            setCreateDocumentDialogOpen(true);
          }}
        >
          <AddIcon className={classes.menuIcon} />
          <Typography>Create blank document</Typography>
        </MenuItem>
      </Menu>
      <UploadFilesPicker
        domContainer={FileStackDomContainer.SendForSignaturePage}
        open={isFilePickerOpen}
        pickerDataCy={uploadPickerDataCy}
        pickerOpts={{
          afterUploadDone: async res => {
            setFilePickerOpen(false);
            const documentIds = res.data?.createFiles?.map(file => file.id) ?? [];
            if (documentIds.length)
              await addDocumentsToWorkflow({
                variables: { input: { documentIds, documentRole, workflowId } },
              });
          },
          onClose: () => setFilePickerOpen(false),
          props: {
            documentVariant:
              workflow?.variant === Apollo.WorkflowVariant.Template
                ? Apollo.DocumentVariant.Template
                : Apollo.DocumentVariant.Document,
            isSigned: false,
          },
        }}
      />
      <AddWorkflowDocumentDialog
        documentVariant={repoPickerVariant ?? undefined}
        open={Boolean(repoPickerVariant)}
        workflowId={workflowId}
        onClose={() => setRepoPickerVariant(null)}
      />
      <NameDialog
        handleClose={() => setCreateDocumentDialogOpen(false)}
        open={createDocumentDialogOpen}
        placeholder="Add a title for your document..."
        variant={NameDialogVariant.CreateDocument}
        onFormikSubmit={async name => {
          const documentVariant = isTemplateWorkflow
            ? Apollo.DocumentVariant.Template
            : Apollo.DocumentVariant.Document;
          const { data } = await createDocument({
            variables: {
              documentProps: {
                name,
                variant: documentVariant,
              },
            },
          });
          if (data?.createDocument.document) {
            await addDocumentsToWorkflow({
              variables: {
                input: { documentIds: [data.createDocument.document.id], documentRole, workflowId },
              },
            });
          }
          setCreateDocumentDialogOpen(false);
        }}
      />
    </>
  );
});
